import { addError, logger } from "@wayflyer/blackbox-fe";
import type { NavIconOverlayProps, NavigationProps, NavItemButton, NavItemLink, NavSection } from "@wayflyer/flyui";
import { NavIconOverlay, ProductLogo, useRiveLoaderQuery, useToast } from "@wayflyer/flyui";
import {
  IconBank20Line,
  IconCity20Line,
  IconCreditCards20Line,
  IconEmptyPage20Line,
  IconGift20Line,
  IconGraphUp20Line,
  IconHeadsetHelp20Line,
  IconHome20Line,
  IconLogOut20Line,
  IconLotOfCash20Line,
  IconOpenBook20Line,
  IconOpenNewWindow20Line,
  IconReports20Line,
  IconSettings20Line,
  IconUserCircle20Line,
  IconWrench20Line,
} from "@wayflyer/flyui-icons/20/line";
import { BaseSchemas } from "api-types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { COMPANY_ROLE_MESSAGES, useErrorModal, useInviteModalActions, useLocalStorage } from "shared-ui";

import { useJoinAnalyticsPilotMutation } from "../../../analytics-pilot/queries";
import { useBookkeepingWaitlistQuery } from "../../../bookkeeping/queries";
import { useIntercomLoaded } from "../intercom";

import { getNavItemNameFromId } from "./nav-item-strings";
import { getNavPlacement, isNavigableNavItem, type NavSection as NavLayoutSection } from "./nav-layout";

declare global {
  interface Window {
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
  }
}

type RiveData = NavIconOverlayProps["riveData"];
type IconRiveConfig = { src: string; gradient?: boolean };

/**
 * Canonical list of nav icon keys used in the sidenav.
 *
 * Adding a key here enforces that getIconComponent handles it (via the switch)
 * and makes it available for ICON_RIVE_MAP. If a new nav item appears from the
 * backend with an icon not in this list, TypeScript will flag the gap.
 */
const NAV_ICON_KEYS = [
  "home",
  "bank",
  "banking",
  "account",
  "payments",
  "cards",
  "transactions",
  "line-chart",
  "documents",
  "share",
  "help",
  "connections",
  "internal",
  "accounting",
  "intelligence",
] as const;

type NavIconKey = (typeof NAV_ICON_KEYS)[number];

/**
 * Maps every NavIconKey to its Rive animation config.
 *
 * Every key in NavIconKey must have an entry here. If a top-level nav icon
 * returned by the backend is missing from this map, Rive overlays are disabled
 * for ALL items to avoid a mix of animated and static icons, and the gap is
 * reported via logger.warn.
 */
const ICON_RIVE_MAP: Record<NavIconKey, IconRiveConfig> = {
  home: { src: "/fly-assets/animations/icon_home-v4.riv" },
  bank: { src: "/fly-assets/animations/icon_a-lot-of-cash-v4.riv" },
  banking: { src: "/fly-assets/animations/icon_bank-a-v4.riv" },
  account: { src: "/fly-assets/animations/icon_bank-a-v4.riv" },
  payments: { src: "/fly-assets/animations/icon_wallet-v4.riv" },
  cards: { src: "/fly-assets/animations/icon_credit-cards-v4.riv" },
  transactions: { src: "/fly-assets/animations/icon_data-transfer-up-v6.riv" },
  "line-chart": { src: "/fly-assets/animations/icon_report-v4.riv" },
  documents: { src: "/fly-assets/animations/icon_multy-pages-v4.riv" },
  share: { src: "/fly-assets/animations/icon_gift-v4.riv" },
  help: { src: "/fly-assets/animations/icon_open-new-window-v4.riv" },
  connections: { src: "/fly-assets/animations/icon_wrech-v4.riv" },
  internal: { src: "/fly-assets/animations/icon_wrench-v4.riv" },
  accounting: { src: "/fly-assets/animations/icon_open-book-v4.riv" },
  intelligence: { src: "/fly-assets/animations/icon_intelligence-v7.riv", gradient: true },
};

export const CUSTOMER_NAV_RIVE_SRCS = [...new Set(Object.values(ICON_RIVE_MAP).map(({ src }) => src))];

function wrapIconWithOverlay(icon: ReactNode, iconKey: NavIconKey, riveData: RiveData | undefined): ReactNode {
  const config = ICON_RIVE_MAP[iconKey];
  if (!riveData?.assets.has(config.src)) {
    return icon;
  }
  return (
    <NavIconOverlay riveSrc={config.src} riveData={riveData} gradient={config.gradient}>
      {icon}
    </NavIconOverlay>
  );
}

function getIconComponent(icon: NavIconKey, riveData?: RiveData): ReactNode {
  let staticIcon: ReactNode;
  switch (icon) {
    case "home":
      staticIcon = <IconHome20Line />;
      break;
    case "bank":
      staticIcon = <IconLotOfCash20Line />;
      break;
    case "line-chart":
      staticIcon = <IconReports20Line />;
      break;
    case "help":
      staticIcon = <IconOpenNewWindow20Line />;
      break;
    case "connections":
      staticIcon = <IconWrench20Line />;
      break;
    case "banking":
      staticIcon = <IconCity20Line />;
      break;
    case "internal":
      staticIcon = <IconWrench20Line />;
      break;
    case "share":
      staticIcon = <IconGift20Line />;
      break;
    case "account":
      staticIcon = <IconBank20Line />;
      break;
    case "payments":
      staticIcon = <IconGraphUp20Line />;
      break;
    case "cards":
      staticIcon = <IconCreditCards20Line />;
      break;
    case "transactions":
      staticIcon = <IconOpenBook20Line />;
      break;
    case "documents":
      staticIcon = <IconEmptyPage20Line />;
      break;
    case "accounting":
      staticIcon = <IconOpenBook20Line />;
      break;
    case "intelligence":
      staticIcon = <ProductLogo.Intelligence />;
      break;
  }
  return wrapIconWithOverlay(staticIcon, icon, riveData);
}

const NAV_ICON_KEY_SET: ReadonlySet<string> = new Set<string>(NAV_ICON_KEYS);

function asNavIconKey(icon: string): NavIconKey | undefined {
  if (NAV_ICON_KEY_SET.has(icon)) {
    return icon as NavIconKey;
  }
  logger.warn(`Unknown nav icon key "${icon}" — add it to NAV_ICON_KEYS, getIconComponent, and ICON_RIVE_MAP`);
  return undefined;
}

const navItemMatches = (path: string, alternativePaths: string[] = [], pathname: string): boolean => {
  if (path === pathname || alternativePaths.includes(pathname)) {
    return true;
  } else {
    return alternativePaths.some((alternativePath) => pathname.startsWith(alternativePath));
  }
};

/**
 * The Accounts nav item is always rendered as a single link to the accounts
 * screen — individual accounts are never surfaced as sub-items.
 */
const isAccountsNavItem = (navItem: BaseSchemas.NavItem): boolean =>
  navItem.id.includes("accounts") || !!navItem.path?.includes("/accounts");

type CustomerNavItem = NavItemLink | NavSection;
// `NavGroup` is not re-exported from the flyui root barrel, so derive the
// grouped-sections shape from the public NavigationProps type.
type CustomerNavGroups = Extract<NonNullable<NavigationProps["sections"]>, { groups: unknown }>["groups"];

/**
 * Groups the top-level nav items into the primary group and the bottom
 * "Products" group. Order and section come from the declarative {@link NAV_LAYOUT}
 * config rather than the server's ordering. Items the config doesn't recognise
 * fall back to the end of the main group so nothing silently disappears.
 */
function buildNavGroups(items: CustomerNavItem[], productsLabel: string): CustomerNavGroups {
  const sectionOf = (item: CustomerNavItem): NavLayoutSection => getNavPlacement(item.id)?.section ?? "main";
  const orderOf = (item: CustomerNavItem): number => getNavPlacement(item.id)?.order ?? Number.MAX_SAFE_INTEGER;
  const itemsInSection = (section: NavLayoutSection): CustomerNavItem[] =>
    items.filter((item) => sectionOf(item) === section).sort((a, b) => orderOf(a) - orderOf(b));

  const productItems = itemsInSection("products");

  return [
    { id: "main", items: itemsInSection("main") },
    ...(productItems.length > 0 ? [{ id: "products", label: productsLabel, items: productItems }] : []),
  ];
}

export const useNavigationItems = ({ appConfig }: { appConfig: BaseSchemas.CustomerAppConfig }): NavigationProps => {
  const intl = useIntl();
  const { navItems, customerDomain } = appConfig;
  // Rive overlays are gated behind the "animated-side-nav-icons" feature flag.
  // Disable overlays if any top-level icon lacks a Rive mapping to avoid a mix
  // of animated and static icons.
  const animatedIconsEnabled = useFeatureFlagEnabled("animated-side-nav-icons");
  const accountingNavEnabled = useFeatureFlagEnabled("accounting-nav-enabled");
  const analyticsPilotEnabled = useFeatureFlagEnabled("conjura-analytics-pilot");
  // New profile-pages IA (PFF-472) — flag-gated nav entry points. `undefined`
  // (flags loading / PostHog outage) is treated as off so the new pages stay dark.
  const profilePagesV2Enabled = useFeatureFlagEnabled("profile_pages_v2") ?? false;
  // The new connections page rolls out on its own flag, independent of the rest of the IA.
  const connectionsPageV2Enabled = useFeatureFlagEnabled("connections_page_v2") ?? false;
  const [analyticsPilotClicked, setAnalyticsPilotClicked] = useLocalStorage<boolean>(
    `analytics-pilot-clicked-${customerDomain}`,
    false,
    true
  );
  const { mutate: joinPilot } = useJoinAnalyticsPilotMutation(customerDomain);
  const allIconsCovered = (navItems ?? []).every((item) => item.icon in ICON_RIVE_MAP);
  const riveResult = useRiveLoaderQuery(CUSTOMER_NAV_RIVE_SRCS);
  const riveData = animatedIconsEnabled && allIconsCovered ? riveResult.data : undefined;
  const { openInviteModal } = useInviteModalActions();
  const alternativePathsMap = {};
  const [messageCount, setMessageCount] = useState(0);
  const messengerOpen = useRef(false);
  const isIntercomLoaded = useIntercomLoaded();
  const { data: entry, isPending, isError } = useBookkeepingWaitlistQuery({ enabled: accountingNavEnabled === true });

  useEffect(() => {
    if (isIntercomLoaded) {
      const handleUnreadCountChange = (unreadCount: number) => {
        setMessageCount(unreadCount);
      };
      const handleHide = () => {
        messengerOpen.current = false;
      };
      const handleShow = () => {
        messengerOpen.current = true;
      };

      window.Intercom("onUnreadCountChange", handleUnreadCountChange);
      window.Intercom("onHide", handleHide);
      window.Intercom("onShow", handleShow);

      return () => {
        // Clean up listeners
        window.Intercom("off", "onUnreadCountChange", handleUnreadCountChange);
        window.Intercom("off", "onHide", handleHide);
        window.Intercom("off", "onShow", handleShow);
      };
    }
  }, [isIntercomLoaded]);

  for (const navItem of navItems ?? []) {
    if (navItem.alternativePaths) {
      alternativePathsMap[navItem.path] = navItem.alternativePaths;
    }

    // Accounts is a single link but must stay highlighted on any /accounts
    // sub-page (e.g. an individual account). Registering its own path as an
    // alternative makes getIsRouteSelected's prefix match cover those routes.
    if (isAccountsNavItem(navItem) && navItem.path) {
      alternativePathsMap[navItem.path] = [...(alternativePathsMap[navItem.path] ?? []), navItem.path];
    }

    if (navItem.subItems) {
      for (const subItem of navItem.subItems) {
        if (subItem.alternativePaths) {
          alternativePathsMap[subItem.path] = subItem.alternativePaths;
        }
      }
    }
  }

  const getIsRouteSelected = (pathname: string): boolean => {
    return navItemMatches(pathname, alternativePathsMap[pathname], location.pathname);
  };

  function getClientSubNavItems(
    navItems: BaseSchemas.CustomerAppConfig["navItems"] | null | undefined
  ): Array<NavItemButton | NavItemLink> | undefined {
    if (!navItems || navItems.length === 0) {
      return;
    }

    return navItems.map(({ id, icon, path }) => {
      const iconKey = asNavIconKey(icon);
      return {
        id,
        href: path,
        icon: iconKey ? getIconComponent(iconKey, riveData) : undefined,
        label: getNavItemNameFromId(intl, id),
      };
    });
  }

  const userProfile: NavigationProps["userProfile"] = {
    name: appConfig.userName,
    email: appConfig.userEmail,
    role: appConfig.userRole ? intl.formatMessage(COMPANY_ROLE_MESSAGES[appConfig.userRole]) : undefined,
    // Empty until staff Slack images land; nav falls back to initials.
    profileImageUrl: appConfig.profileImgUrl || undefined,
  };

  const waitlistSettled = !isPending && !isError;
  const toaster = useToast();
  const { openErrorModal } = useErrorModal();
  const hasJoinedWaitlist = waitlistSettled && !!entry;
  const onboardedToBookkeeping = hasJoinedWaitlist && entry?.state === "onboarded";
  const hideAccountingNavItem = waitlistSettled && entry?.state === "not_eligible";
  const isNavItemLink = (item: NavItemLink | NavSection): item is NavItemLink => {
    return !("items" in item);
  };
  const toClientNavItem = (navItem: BaseSchemas.NavItem): NavItemLink | NavSection => {
    const { id } = navItem;
    const iconKey = asNavIconKey(navItem.icon);
    const icon = iconKey ? getIconComponent(iconKey, riveData) : undefined;
    const label = getNavItemNameFromId(intl, navItem.id);
    // Some top-level items (e.g. Performance) are themselves links in addition
    // to grouping their sub-items — see NAVIGABLE_NAV_IDS in nav-layout.
    const navigableSection = isNavigableNavItem(id);

    // Accounts is always a single link to the accounts screen; we never surface
    // individual accounts as sub-items. Active state on /accounts sub-pages is
    // handled via the alternativePaths registration above.
    if (isAccountsNavItem(navItem)) {
      return {
        id,
        icon,
        label,
        href: navItem.path,
      };
    }

    const items = getClientSubNavItems(navItem.subItems);

    if (items) {
      return {
        id,
        icon,
        label,
        items,
        ...(navigableSection && {
          href: navItem.path,
        }),
      };
    } else {
      return {
        id,
        icon,
        label,
        href: navItem.path,
      };
    }
  };

  const userSection: NavigationProps["userSection"] = {
    label: appConfig.userName,
    id: "user-section",
    icon: null,
    items: [],
    sections: [
      {
        id: "user-actions",
        items: [
          ...(profilePagesV2Enabled
            ? [
                {
                  id: "profile",
                  label: intl.formatMessage({ id: "Menu: Profile", defaultMessage: "Profile" }),
                  // User-scoped, mounted domain-less so it's reachable before a company exists.
                  href: "/settings/account",
                  icon: <IconUserCircle20Line />,
                },
              ]
            : []),
          ...(navItems ?? [])
            .map(toClientNavItem)
            .filter(isNavItemLink)
            .filter((item) => getNavPlacement(item.id)?.section === "user"),
          ...(window?.Optanon?.ToggleInfoDisplay
            ? [
                {
                  id: "privacy-settings",
                  label: intl.formatMessage({
                    id: "Privacy settings",
                    defaultMessage: "Privacy preferences",
                  }),
                  onClick: () => window.Optanon.ToggleInfoDisplay(),
                  icon: <IconSettings20Line />,
                },
              ]
            : []),
        ],
      },
      {
        id: "auth-actions",
        items: [
          {
            id: "sign-out",
            label: intl.formatMessage({ id: "Sign out", defaultMessage: "Sign out" }),
            critical: true,
            href: "/authn/logout",
            reloadDocument: true,
            icon: <IconLogOut20Line />,
          },
        ],
      },
    ],
  };

  const clientNavItems = (navItems ?? [])
    .map(toClientNavItem)
    .map((item) => {
      if (item.id === "accounting page title" && onboardedToBookkeeping) {
        return { ...item, external: true };
      }
      // Point the main-nav Connections item at the new page when it's live, so it's the
      // entry point whenever the company profile menu (which otherwise owns Connections
      // under profile_pages_v2) isn't shown.
      if (item.id === "connections page title" && connectionsPageV2Enabled && "href" in item) {
        return { ...item, href: `/${customerDomain}/settings/company/connections` };
      }
      return item;
    })
    .filter((item) => !(hideAccountingNavItem && item.id === "accounting page title")) //@todo: do this on the server
    // When the full profile IA is on, Connections lives in the company profile menu, so
    // hide the redundant main-nav item. With only connections_page_v2 on, the (repointed)
    // main-nav item stays as the entry point.
    .filter((item) => !(profilePagesV2Enabled && connectionsPageV2Enabled && item.id === "connections page title"));

  // Items the layout config routes to the company profile menu (e.g. Documents)
  // rather than the side-nav. buildNavGroups excludes these from the side-nav.
  //
  // Invariant: `company`-routed items only render when the company profile menu
  // does, i.e. when `customerName` is set. This is safe because nav items are
  // only ever returned for a real company (see customershared/app_config.py),
  // and `Customer.name` is required (CharField, blank=False) — so whenever a
  // company-routed item exists, a company name exists too.
  const companyMenuNavItems = clientNavItems
    .filter((item) => getNavPlacement(item.id)?.section === "company")
    .sort((a, b) => (getNavPlacement(a.id)?.order ?? 0) - (getNavPlacement(b.id)?.order ?? 0));

  // When `profile_pages_v2` is on, the company menu surfaces the new IA pages
  // in place of the server-driven Documents item + legacy Settings link. We list
  // only pages that have a built screen (Profile, Documents, Users; Connections is
  // gated separately on `connections_page_v2`); the design's Settings/Rules entries
  // are deferred until those pages exist.
  // Invite stays in both modes. The main-nav Connections item is hidden for v2 (see
  // clientNavItems above) since Connections now lives in this menu; retiring the old
  // pages themselves is redirect-cleanup (a later ticket).
  const companyProfileMenuItems = [
    {
      id: "company-profile",
      label: intl.formatMessage({ id: "Menu: Company profile", defaultMessage: "Profile" }),
      href: `/${customerDomain}/settings/company`,
    },
    {
      id: "company-documents",
      label: intl.formatMessage({ id: "Menu: Company documents", defaultMessage: "Documents" }),
      href: `/${customerDomain}/settings/company/documents`,
    },
    {
      id: "company-users",
      label: intl.formatMessage({ id: "Menu: Company users", defaultMessage: "Users" }),
      href: `/${customerDomain}/settings/company/users`,
    },
    // Connections has its own flag, so only surface it here once that page is live.
    ...(connectionsPageV2Enabled
      ? [
          {
            id: "company-connections",
            label: intl.formatMessage({ id: "Menu: Company connections", defaultMessage: "Connections" }),
            href: `/${customerDomain}/settings/company/connections`,
          },
        ]
      : []),
  ];

  const legacyCompanyMenuItems = [
    ...companyMenuNavItems.flatMap((item) =>
      "href" in item ? [{ id: item.id, label: item.label, href: item.href }] : []
    ),
    {
      id: "settings",
      label: intl.formatMessage({ id: "Menu: Settings", defaultMessage: "Settings" }),
      href: `/${appConfig?.customerDomain}/settings`,
    },
  ];

  const companySection: NavigationProps["companySection"] =
    appConfig.customerName?.length > 0
      ? {
          label: appConfig.customerName,
          id: "company-section",
          icon: null,
          logoUrl: appConfig.companyLogoUrl || undefined,
          items: [
            ...(profilePagesV2Enabled ? companyProfileMenuItems : legacyCompanyMenuItems),
            {
              id: "invite",
              label: intl.formatMessage({ id: "Invite someone", defaultMessage: "Invite a team member" }),
              onClick: () => openInviteModal(),
            },
          ],
        }
      : undefined;

  const sections: NavigationProps["sections"] = {
    groups: buildNavGroups(
      clientNavItems,
      intl.formatMessage({ id: "products section title", defaultMessage: "Products" })
    ),
  };

  const infoItems = [
    ...(isIntercomLoaded
      ? [
          {
            id: "support",
            label: intl.formatMessage({ id: "support title", defaultMessage: "Support" }),
            onClick: () => {
              if ("Intercom" in window) {
                if (messengerOpen.current) {
                  window.Intercom("hide");
                } else {
                  window.Intercom("show");
                }
              }
            },
            icon: <IconHeadsetHelp20Line />,
          },
        ]
      : []),
  ] as Array<NavItemButton | NavItemLink>;

  const notificationBubbles: Array<{ navItemId: string; label: string; title?: string }> = [];
  if (messageCount > 0) {
    notificationBubbles.push({
      navItemId: "support",
      label: String(messageCount),
      title: `${messageCount} messages require your attention`,
    });
  }
  if (waitlistSettled && !hasJoinedWaitlist) {
    notificationBubbles.push({
      navItemId: "accounting page title",
      label: "New",
      title: "Join the waitlist!",
    });
  }
  if ((navItems ?? []).some((item) => item.id === "intelligence page title")) {
    notificationBubbles.push({
      navItemId: "intelligence page title",
      label: "New",
    });
  }

  return {
    userProfile,
    userSection,
    companySection,
    sections,
    infoItems,
    getIsRouteSelected,
    notificationBubbles: notificationBubbles.length > 0 ? notificationBubbles : undefined,
    alwaysVisibleItemIds: ["invite"],
    enableCollapse: appConfig.featureFlags?.enableCollapsingSideNav ?? false,
    globalCtas:
      analyticsPilotEnabled && !analyticsPilotClicked
        ? [
            {
              message: intl.formatMessage({
                id: "Analytics pilot banner: message",
                defaultMessage: "Join to get early access to our new AI agent: Wayflyer Intelligence",
              }),
              action: {
                content: intl.formatMessage({
                  id: "Analytics pilot banner: cta",
                  defaultMessage: "Join Now",
                }),
                analyticsId: "analytics-pilot-join-banner",
                onAction: () => {
                  joinPilot(undefined, {
                    onSuccess: () => {
                      setAnalyticsPilotClicked(true);
                      toaster.positive(
                        intl.formatMessage({
                          id: "bookkeeping.waitlist.statusSql",
                          defaultMessage: "Thanks — we’ve received your details and will be in touch.",
                        })
                      );
                    },
                    onError: (error: unknown) => {
                      addError("Error joining analytics pilot");
                      openErrorModal({
                        variant: "submit-error",
                        error: new Error("Failed to join analytics pilot", { cause: error }),
                      });
                    },
                  });
                },
              },
              color: "black" as const,
              matchRoutes: ["/:customerDomain/performance/*"],
            },
          ]
        : undefined,
  };
};
